import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Edit3, Download, FileSpreadsheet } from "lucide-react";
import { formatAuDate } from "@/lib/date";

function computeAge(dobIso) {
  if (!dobIso) return "";
  const dob = new Date(dobIso);
  if (isNaN(dob.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : "";
}

async function downloadBlob(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 250);
    toast.success("Export downloaded");
  } catch (e) {
    toast.error(e.response?.data?.detail || "Export failed");
  }
}

export function VolunteersView({ eventId, alloc }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [resend, setResend] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("first_name"); // 'first_name' | 'last_name' | 'registered'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/events/${eventId}/volunteers`);
      // Group by itinerary_id (or id) so one person = one row
      const byPerson = new Map();
      (data || []).forEach((v) => {
        const key = v.itinerary_id || v.id;
        if (!byPerson.has(key)) byPerson.set(key, { ...v, volunteer_id: v.id, itinerary_rows: [] });
        byPerson.get(key).itinerary_rows.push({ assigned: v.assigned, requested: v.requested, status: v.status });
      });
      setRows(Array.from(byPerson.values()));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load volunteers");
    } finally { setLoading(false); }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  // Split "Full Name" if first/last aren't stored (older records) — used purely for sorting fallback.
  function nameParts(v) {
    if (v.first_name || v.last_name) return { first: (v.first_name || "").trim(), last: (v.last_name || "").trim() };
    const parts = (v.name || "").trim().split(/\s+/);
    if (parts.length <= 1) return { first: parts[0] || "", last: "" };
    return { first: parts[0], last: parts.slice(1).join(" ") };
  }
  // Wrapped in useMemo so React only recomputes when rows or sortBy changes —
  // more importantly, guarantees a fresh reference on every sortBy click so the
  // table body re-renders even if the previous array had the same identity.
  const sortedRows = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      if (sortBy === "registered") {
        const ta = a.created_at || ""; const tb = b.created_at || "";
        return tb.localeCompare(ta); // newest first
      }
      const pa = nameParts(a); const pb = nameParts(b);
      if (sortBy === "last_name") {
        // Case-insensitive; empty last-name falls back to first name so sort is stable.
        return (pa.last || pa.first || "").localeCompare(pb.last || pb.first || "", undefined, { sensitivity: "base" })
          || (pa.first || "").localeCompare(pb.first || "", undefined, { sensitivity: "base" });
      }
      // default: first_name
      return (pa.first || "").localeCompare(pb.first || "", undefined, { sensitivity: "base" })
        || (pa.last || "").localeCompare(pb.last || "", undefined, { sensitivity: "base" });
    });
    return arr;
  }, [rows, sortBy]);
  function startEdit(v) {
    setEditing(v);
    setResend(false);
    setForm({
      name: v.name || "", email: v.email || "", mobile: v.mobile || "",
      ec1_name: v.emergency_contact_1?.name || "", ec1_mobile: v.emergency_contact_1?.mobile || "",
      ec2_name: v.emergency_contact_2?.name || "", ec2_mobile: v.emergency_contact_2?.mobile || "", location: v.location || "",
      dietary: v.dietary || "", medical_notes: v.medical_notes || "", event_experience: v.event_experience || "",
      organised_group: v.organised_group || "",
    });
  }
  const emailChanged = editing && form.email && (form.email.trim().toLowerCase() !== (editing.email || "").trim().toLowerCase());
  const mobileChanged = editing && form.mobile && (form.mobile.trim() !== (editing.mobile || "").trim());
  const isPending = editing && (editing.status === "pending");
  const showResendOption = !!(isPending && (emailChanged || mobileChanged));
  async function saveProfile() {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/admin/events/${eventId}/volunteers/${editing.volunteer_id}`, form);
      if (showResendOption && resend) {
        try {
          const r = await api.post(`/admin/events/${eventId}/volunteers/${editing.volunteer_id}/resend-confirmation`, {
            email: !!emailChanged,
            sms: !!mobileChanged,
          });
          const parts = [];
          if (r.data?.email_sent) parts.push("email");
          if (r.data?.sms_sent) parts.push("SMS");
          toast.success(`Profile updated · confirmation ${parts.length ? "re-sent by " + parts.join(" & ") : "resend requested"}`);
        } catch (rerr) {
          toast.success("Profile updated");
          toast.error(rerr.response?.data?.detail || "Resend failed — please retry");
        }
      } else {
        toast.success("Profile updated");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }
  return (
    <div className="border border-black/10 bg-white p-6" data-testid="volunteers-view">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#002FA7]">Registrant profiles</div>
          <h2 className="mt-1 font-display text-2xl font-black tracking-tighter">All registrants — full profile view</h2>
          <p className="mt-1 text-xs text-zinc-600">Everything captured at signup. Click Edit to correct any field · changes update every itinerary row for this person.</p>
        </div>
        <div className="flex flex-col items-stretch gap-2 md:items-end">
          <button
            onClick={() => downloadBlob(`/admin/events/${eventId}/export`, `volunteers-full-allocations.xlsx`)}
            data-testid="export-full-allocations-btn"
            className="inline-flex items-center justify-center gap-1.5 border-2 border-[#002FA7] bg-white px-4 py-2 text-xs font-semibold text-[#002FA7] hover:bg-[#002FA7] hover:text-white"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" /> Export full allocations (all days · roles · people)
          </button>
          <button
            onClick={() => downloadBlob(`/admin/events/${eventId}/export/volunteers`, `all-volunteers-and-chase-list.xlsx`)}
            data-testid="export-volunteers-btn"
            className="inline-flex items-center justify-center gap-1.5 border-2 border-black bg-black px-4 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black"
          >
            <Download className="h-3.5 w-3.5" /> Export volunteers + chase list (one row per person)
          </button>
        </div>
      </div>
      {loading ? (
        <p className="mt-4 text-xs italic text-zinc-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-4 border border-dashed border-black/15 p-8 text-center text-sm italic text-zinc-500">No registrants yet.</p>
      ) : (
        <>
        {/* Sort controls */}
        <div className="mt-4 flex flex-wrap items-center gap-3 border border-black/10 bg-[#F9F9FB] px-3 py-2 text-xs" data-testid="volunteers-sort-bar">
          <span className="font-medium uppercase tracking-widest text-zinc-500">Sort by</span>
          <div className="inline-flex border border-black/20 bg-white">
            {[
              { v: "first_name", label: "First name" },
              { v: "last_name", label: "Last name" },
              { v: "registered", label: "Newest registration" },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => setSortBy(o.v)}
                data-testid={`sort-${o.v.replace("_", "-")}`}
                className={`px-3 py-1.5 text-[11px] font-semibold ${sortBy === o.v ? "bg-black text-white" : "text-black hover:bg-zinc-100"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <span className="ml-auto italic text-zinc-500" data-testid="volunteers-total-count">{rows.length} registrant{rows.length !== 1 ? "s" : ""}</span>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full border-collapse text-xs">
            <thead className="bg-[#F4F4F5]">
              <tr>
                {[
                  "Name", "Age", "Email", "Mobile", "Location", "Shirt", "Dietary", "Medical notes",
                  "Event experience", "Group", "EC #1", "EC #2", "Originally requested", "Itinerary (current)", "Status", "",
                ].map((h) => (
                  <th key={h} className="border-b border-black/10 px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((v) => {
                const age = computeAge(v.date_of_birth);
                const status = v.status === "pending" ? "Pending" : v.status === "confirmed" ? "Confirmed" : v.status === "declined" ? "Declined" : v.status === "unallocated" ? "Unallocated" : v.status || "—";
                const statusCls = v.status === "confirmed" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                  : v.status === "declined" ? "bg-red-100 text-red-800 border-red-200"
                  : v.status === "unallocated" ? "bg-blue-100 text-blue-800 border-blue-200"
                  : "bg-amber-100 text-amber-800 border-amber-200";
                const itinerary = (v.itinerary_rows || [])
                  .map((r) => r.assigned ? `${formatAuDate(r.assigned.day)} · ${r.assigned.session} · ${r.assigned.role}` : r.requested?.day ? `${formatAuDate(r.requested.day)} (awaiting)` : "")
                  .filter(Boolean);
                // Build an "originally requested" list — what the volunteer
                // typed into the form at signup, before any admin drag-drop
                // or reassignment moved them. Kept separate from the "current"
                // itinerary column so admins can audit what changed.
                const requested = (v.itinerary_rows || [])
                  .map((r) => {
                    const req = r.requested || {};
                    if (!req.day && !req.session && !req.role) return "";
                    if (req.session === "(unallocated)" || (!req.session && !req.role)) {
                      return req.day ? `${formatAuDate(req.day)} · organiser to pick a role` : "";
                    }
                    const time = req.start || req.end ? ` (${req.start || "?"}–${req.end || "?"})` : "";
                    return `${req.day ? formatAuDate(req.day) : ""} · ${req.session || "?"}${time} · ${req.role || "?"}`;
                  })
                  .filter(Boolean);
                return (
                  <tr key={v.volunteer_id} data-testid={`volunteer-row-${v.volunteer_id}`} className="align-top hover:bg-[#F9F9FB]">
                    <td className="border-b border-black/5 px-3 py-2 font-semibold whitespace-nowrap">{v.name}</td>
                    <td className="border-b border-black/5 px-3 py-2 font-mono text-center">{age !== "" ? age : <span className="italic text-zinc-400">—</span>}</td>
                    <td className="border-b border-black/5 px-3 py-2 font-mono">{v.email}</td>
                    <td className="border-b border-black/5 px-3 py-2 font-mono whitespace-nowrap">{v.mobile}</td>
                    <td className="border-b border-black/5 px-3 py-2" data-testid={`vol-location-${v.id}`}>{v.location || <span className="italic text-zinc-400">—</span>}</td>
                    <td className="border-b border-black/5 px-3 py-2 font-mono">{v.shirt_size || <span className="italic text-zinc-400">—</span>}</td>
                    <td className="border-b border-black/5 px-3 py-2 max-w-[150px]">{v.dietary || <span className="italic text-zinc-400">—</span>}</td>
                    <td className="border-b border-black/5 px-3 py-2 max-w-[200px] text-[11px]">{v.medical_notes || <span className="italic text-zinc-400">—</span>}</td>
                    <td className="border-b border-black/5 px-3 py-2 max-w-[220px] text-[11px]">{v.event_experience ? `${v.event_experience.slice(0, 120)}${v.event_experience.length > 120 ? "…" : ""}` : <span className="italic text-zinc-400">—</span>}</td>
                    <td className="border-b border-black/5 px-3 py-2 whitespace-nowrap">{v.organised_group || <span className="italic text-zinc-400">—</span>}</td>
                    <td className="border-b border-black/5 px-3 py-2 text-[11px]">{v.emergency_contact_1?.name ? <>{v.emergency_contact_1.name}<br /><span className="font-mono">{v.emergency_contact_1.mobile}</span></> : <span className="italic text-zinc-400">—</span>}</td>
                    <td className="border-b border-black/5 px-3 py-2 text-[11px]">{v.emergency_contact_2?.name ? <>{v.emergency_contact_2.name}<br /><span className="font-mono">{v.emergency_contact_2.mobile}</span></> : <span className="italic text-zinc-400">—</span>}</td>
                    <td className="border-b border-black/5 px-3 py-2 max-w-[220px] text-[11px]" data-testid={`vol-originally-requested-${v.volunteer_id}`}>
                      {requested.length ? requested.map((line, i) => <div key={i}>{line}</div>) : <span className="italic text-zinc-400">—</span>}
                    </td>
                    <td className="border-b border-black/5 px-3 py-2 max-w-[200px] text-[11px]">
                      {itinerary.length ? itinerary.map((line, i) => <div key={i}>{line}</div>) : <span className="italic text-zinc-400">—</span>}
                    </td>
                    <td className="border-b border-black/5 px-3 py-2 whitespace-nowrap"><span className={`inline-block border px-2 py-1 text-[10px] font-black uppercase tracking-widest ${statusCls}`}>{status}</span></td>
                    <td className="border-b border-black/5 px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => startEdit(v)} data-testid={`edit-volunteer-${v.volunteer_id}`} className="inline-flex items-center gap-1 border border-black/20 px-2 py-1 hover:bg-black hover:text-white"><Edit3 className="h-3 w-3" /> Edit</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
      {editing ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setEditing(null)} data-testid="volunteer-edit-modal">
          <div className="w-full max-w-2xl border border-black/10 bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-baseline justify-between">
              <h3 className="font-display text-xl font-black tracking-tighter">Edit {editing.name}</h3>
              <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-black">×</button>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Name</span><input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="input" data-testid="edit-vol-name" /></label>
              <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Mobile</span><input value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} className="input" data-testid="edit-vol-mobile" /></label>
              <label className="text-xs md:col-span-2"><span className="mb-1 block font-medium text-zinc-500">Location</span>
                <select value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="input" data-testid="edit-vol-location">
                  {["", "Adelaide", "Regional SA", "Interstate"].map((l) => <option key={l || "none"} value={l}>{l || "— select —"}</option>)}
                </select></label>
              <label className="text-xs md:col-span-2"><span className="mb-1 block font-medium text-zinc-500">Email</span><input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="input" data-testid="edit-vol-email" /></label>
              <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">EC #1 name</span><input value={form.ec1_name} onChange={(e) => setForm((f) => ({ ...f, ec1_name: e.target.value }))} className="input" data-testid="edit-vol-ec1-name" /></label>
              <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">EC #1 mobile</span><input value={form.ec1_mobile} onChange={(e) => setForm((f) => ({ ...f, ec1_mobile: e.target.value }))} className="input" data-testid="edit-vol-ec1-mobile" /></label>
              <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">EC #2 name</span><input value={form.ec2_name} onChange={(e) => setForm((f) => ({ ...f, ec2_name: e.target.value }))} className="input" data-testid="edit-vol-ec2-name" /></label>
              <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">EC #2 mobile</span><input value={form.ec2_mobile} onChange={(e) => setForm((f) => ({ ...f, ec2_mobile: e.target.value }))} className="input" data-testid="edit-vol-ec2-mobile" /></label>
              <label className="text-xs md:col-span-2"><span className="mb-1 block font-medium text-zinc-500">Event experience</span><textarea value={form.event_experience} onChange={(e) => setForm((f) => ({ ...f, event_experience: e.target.value.slice(0, 1000) }))} className="input min-h-[60px]" data-testid="edit-vol-experience" /></label>
              <label className="text-xs md:col-span-2">
                <span className="mb-1 block font-medium text-zinc-500">Organised group</span>
                <input
                  value={form.organised_group || ""}
                  onChange={(e) => setForm((f) => ({ ...f, organised_group: e.target.value.slice(0, 120) }))}
                  list="vol-edit-groups"
                  placeholder="Type or pick a group (leave blank for none)"
                  data-testid="edit-vol-group"
                  className="input"
                />
                <datalist id="vol-edit-groups">
                  {Array.from(new Set(rows.map((r) => (r.organised_group || "").trim()).filter(Boolean))).sort().map((g) => (
                    <option key={g} value={g} />
                  ))}
                </datalist>
              </label>
              <label className="text-xs md:col-span-2"><span className="mb-1 block font-medium text-zinc-500">Medical notes</span><textarea value={form.medical_notes} onChange={(e) => setForm((f) => ({ ...f, medical_notes: e.target.value.slice(0, 1000) }))} className="input min-h-[50px]" /></label>
            </div>
            {showResendOption ? (
              <div className="mt-4 border border-[#002FA7]/40 bg-[#002FA7]/[0.05] p-3 text-xs" data-testid="resend-confirmation-row">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={resend}
                    onChange={(e) => setResend(e.target.checked)}
                    data-testid="resend-confirmation-checkbox"
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-semibold text-[#002FA7]">Resend pending confirmation to the new address</div>
                    <div className="mt-0.5 text-zinc-600">
                      {emailChanged && mobileChanged ? "Confirmation email + SMS will be re-sent."
                        : emailChanged ? "Confirmation email will be re-sent to the new address."
                        : "Confirmation SMS will be re-sent to the new mobile."}
                    </div>
                  </div>
                </label>
              </div>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="border border-black/20 px-3 py-2 text-xs hover:bg-black hover:text-white">Cancel</button>
              <button onClick={saveProfile} disabled={saving} data-testid="edit-vol-save" className="border border-black bg-black px-4 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black disabled:opacity-50">{saving ? "Saving…" : "Save changes"}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
